import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X, Plus, Eye, Mail, Phone, AlertTriangle } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  useCreateEventFlow,
  useUpdateEventFlow,
  useEventFlows,
  useCheckRecipients,
  useBookingStatuses,
  type CreateEventFlowData,
  type UpdateEventFlowData,
} from "@/hooks/useEventManagement";
import { useCommunicationTemplates } from "@/hooks/useEventSystem";

interface EventFlowFormProps {
  flowId?: string | null;
  onClose: () => void;
}

interface FormData extends Omit<CreateEventFlowData, 'recipient_config'> {
  is_active: boolean;
  channels_array: string[];
  recipient_types: string[];
  custom_emails: string[];
  target_statuses: string[]; // NEW: Array of selected status keys
}

const COMMUNICATION_CHANNELS = [
  { value: "email", label: "Email" },
  { value: "message", label: "Message (SMS/WhatsApp)" },
  { value: "webhook", label: "Webhook" },
];

const RECIPIENT_TYPES = [
  { value: "customer", label: "Customer" },
  { value: "collection_contact", label: "Collection Contact" },
  { value: "delivery_contact", label: "Delivery Contact" },
  { value: "org_admin", label: "Organization Admin" },
  { value: "org_member", label: "Organization Member" },
  { value: "platform_admin", label: "Platform Admin" },
];

// Helper function to determine which channels a template supports
const getTemplateChannels = (template: any): string[] => {
  if (!template) return [];
  
  const channels: string[] = [];
  
  // Email support: check if email templates exist
  if (template.email_subject_template?.trim() || template.email_content_template?.trim()) {
    channels.push('email');
  }
  
  // Message support: check if SMS, WhatsApp, or push templates exist
  if (template.sms_content_template?.trim() || template.whatsapp_content_template?.trim()) {
    channels.push('message');
  }
  
  // Webhook support: check if webhook URL exists
  if (template.webhook_url?.trim()) {
    channels.push('webhook');
  }
  
  return channels;
};

export function EventFlowForm({ flowId, onClose }: EventFlowFormProps) {
  const [customEmails, setCustomEmails] = useState<string[]>([]);
  const [recipientPreview, setRecipientPreview] = useState<any>(null);
  const [recipientLoading, setRecipientLoading] = useState(false);

  const { data: eventFlows } = useEventFlows();
  const { data: statuses } = useBookingStatuses();
  const { data: templates } = useCommunicationTemplates();
  const createFlow = useCreateEventFlow();
  const updateFlow = useUpdateEventFlow();
  const checkRecipients = useCheckRecipients();

  const currentFlow = flowId 
    ? eventFlows?.find(f => f.id === flowId)
    : null;

  const form = useForm<FormData>({
    defaultValues: {
      name: "",
      description: "",
      from_status: "",
      target_statuses: [], // NEW: Empty array for multi-select
      template_id: "",
      channels: [],
      delay_minutes: 0,
      priority: 1,
      conditions: {},
      is_active: true,
      channels_array: [],
      recipient_types: [],
      custom_emails: [],
    },
  });

  // Watch template selection to filter channels
  const selectedTemplateId = form.watch("template_id");
  const selectedTemplate = templates?.find(t => t.id === selectedTemplateId);
  const supportedChannels = getTemplateChannels(selectedTemplate);

  // Populate form when editing existing flow
  useEffect(() => {
    if (currentFlow) {
      const recipientConfig = currentFlow.recipient_config as any;
      form.reset({
        name: currentFlow.name,
        description: currentFlow.description || "",
        from_status: currentFlow.from_status || "",
        target_statuses: currentFlow.target_statuses || [currentFlow.to_status], // NEW: Migrate old data
        template_id: currentFlow.template_id,
        channels: currentFlow.channels,
        delay_minutes: currentFlow.delay_minutes,
        priority: currentFlow.priority,
        conditions: currentFlow.conditions || {},
        is_active: currentFlow.is_active,
        channels_array: currentFlow.channels,
        recipient_types: recipientConfig.types || [],
        custom_emails: recipientConfig.custom_emails || [],
      });
      setCustomEmails(recipientConfig.custom_emails || []);
    }
  }, [currentFlow, form]);

  // Auto-deselect channels when template changes and they're no longer supported
  useEffect(() => {
    if (!selectedTemplateId) return;
    
    const currentChannels = form.getValues("channels_array") || [];
    const unsupportedChannels = currentChannels.filter(
      channel => !supportedChannels.includes(channel)
    );
    
    if (unsupportedChannels.length > 0) {
      const newChannels = currentChannels.filter(
        channel => supportedChannels.includes(channel)
      );
      form.setValue("channels_array", newChannels);
      
      toast({
        title: "Channels Updated",
        description: `${unsupportedChannels.join(", ")} removed - not supported by selected template`,
        variant: "default",
      });
    }
  }, [selectedTemplateId, supportedChannels, form]);

  const onSubmit = async (data: FormData) => {
    // Validate that at least one target status is selected
    if (!data.target_statuses || data.target_statuses.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one target status",
        variant: "destructive",
      });
      return;
    }

    // Validate that at least one recipient type or custom email is provided
    if (data.recipient_types.length === 0 && customEmails.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one recipient type or add a custom email address",
        variant: "destructive",
      });
      return;
    }

    // Validate that at least one channel is selected
    if (!data.channels_array || data.channels_array.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one communication channel",
        variant: "destructive",
      });
      return;
    }

    // Validate that all selected channels are supported by the template
    const unsupportedChannels = data.channels_array.filter(
      channel => !supportedChannels.includes(channel)
    );
    if (unsupportedChannels.length > 0) {
      toast({
        title: "Invalid Channels",
        description: `The following channels are not supported by the selected template: ${unsupportedChannels.join(", ")}`,
        variant: "destructive",
      });
      return;
    }

    const flowData = {
      name: data.name,
      description: data.description,
      from_status: data.from_status || null, // NULL for creation events
      target_statuses: data.target_statuses, // NEW: Array of statuses
      template_id: data.template_id,
      channels: data.channels_array,
      recipient_config: {
        types: data.recipient_types,
        custom_emails: customEmails,
      },
      delay_minutes: data.delay_minutes,
      priority: data.priority,
      conditions: data.conditions || {},
    };

    try {
      if (flowId) {
        await updateFlow.mutateAsync({
          id: flowId,
          updates: {
            ...flowData,
            is_active: data.is_active,
          },
        });
      } else {
        await createFlow.mutateAsync(flowData);
      }
      onClose();
    } catch (error) {
      console.error("Failed to save event flow:", error);
    }
  };

  const handleAddCustomEmail = (email: string) => {
    if (email && !customEmails.includes(email)) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast({
          title: "Invalid Email",
          description: "Please enter a valid email address",
          variant: "destructive",
        });
        return;
      }
      setCustomEmails([...customEmails, email]);
    }
  };

  const handleRemoveCustomEmail = (email: string) => {
    setCustomEmails(customEmails.filter(e => e !== email));
  };

  const handleCheckRecipients = async () => {
    const recipientTypes = form.getValues("recipient_types");
    if (recipientTypes.length === 0 && customEmails.length === 0) {
      toast({
        title: "No Recipients",
        description: "Please select recipient types or add custom emails to preview",
        variant: "destructive",
      });
      return;
    }

    setRecipientLoading(true);
    try {
      const result = await checkRecipients.mutateAsync({
        recipientConfig: {
          types: recipientTypes,
          custom_emails: customEmails,
        },
        bookingId: "demo-booking-id",
      });
      setRecipientPreview(result);
    } catch (error) {
      console.error("Failed to check recipients:", error);
    } finally {
      setRecipientLoading(false);
    }
  };

  const fromStatus = form.watch("from_status");
  const targetStatuses = form.watch("target_statuses");
  const fromStatusInfo = statuses?.find(s => s.status_key === fromStatus);

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>
          {flowId ? "Edit Event Flow" : "Create Event Flow"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Flow Name*</Label>
              <Input
                {...form.register("name", { required: true })}
                placeholder="e.g., Customer Quote Notification"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="template_id">Communication Template*</Label>
              <Controller
                name="template_id"
                control={form.control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates?.filter(t => t.is_active).map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex items-center gap-2">
                            <span>{template.name}</span>
                            <Badge variant="outline" className="text-xs">
                              {template.category}
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              {...form.register("description")}
              placeholder="Describe when this flow should trigger and what it does"
              rows={3}
            />
          </div>

          {/* Status Transition (Open Architecture) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="from_status">From Status</Label>
              <Controller
                name="from_status"
                control={form.control}
                render={({ field }) => (
                  <Select 
                    value={field.value || "__ANY__"} 
                    onValueChange={(value) => field.onChange(value === "__ANY__" ? "" : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Any status (for creation events)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__ANY__">
                        <span className="text-muted-foreground">Any status (creation event)</span>
                      </SelectItem>
                      {statuses?.filter(s => s.is_active).map((status) => (
                        <SelectItem key={status.status_key} value={status.status_key}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full border"
                              style={{ backgroundColor: status.color }}
                            />
                            {status.display_name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="target_statuses">Target Statuses*</Label>
              <Controller
                name="target_statuses"
                control={form.control}
                rules={{ 
                  required: "At least one target status is required",
                  validate: (value) => value && value.length > 0 || "Select at least one status"
                }}
                render={({ field }) => (
                  <div className="space-y-2">
                    {/* Selected Status Badges */}
                    <div className="flex flex-wrap gap-2 min-h-[40px] p-2 border rounded-md bg-card">
                      {!field.value || field.value.length === 0 ? (
                        <span className="text-sm text-muted-foreground">
                          Select target statuses...
                        </span>
                      ) : (
                        field.value.map(statusKey => {
                          const status = statuses?.find(s => s.status_key === statusKey);
                          return status ? (
                            <Badge
                              key={statusKey}
                              style={{ backgroundColor: status.color, color: 'white' }}
                            >
                              {status.display_name}
                              <X
                                className="ml-1 h-3 w-3 cursor-pointer"
                                onClick={() => {
                                  field.onChange(
                                    field.value.filter(s => s !== statusKey)
                                  );
                                }}
                              />
                            </Badge>
                          ) : null;
                        })
                      )}
                    </div>

                    {/* Status Selection Dropdown */}
                    <Select
                      onValueChange={(value) => {
                        if (!field.value?.includes(value)) {
                          field.onChange([...(field.value || []), value]);
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Add status..." />
                      </SelectTrigger>
                      <SelectContent>
                        {statuses
                          ?.filter(s => s.is_active && !field.value?.includes(s.status_key))
                          .map(status => (
                            <SelectItem key={status.status_key} value={status.status_key}>
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: status.color }}
                                />
                                {status.display_name}
                              </div>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              />
              {form.formState.errors.target_statuses && (
                <p className="text-sm text-red-500 mt-1">
                  {form.formState.errors.target_statuses.message}
                </p>
              )}
            </div>
          </div>

          {(fromStatusInfo || (targetStatuses && targetStatuses.length > 0)) && (
            <div className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-md">
              This flow will trigger when booking status changes from{" "}
              <span className="font-semibold">{fromStatusInfo?.display_name || "any status"}</span> to{" "}
              {targetStatuses && targetStatuses.length > 0 ? (
                targetStatuses.length === 1 ? (
                  <span className="font-semibold">
                    {statuses?.find(s => s.status_key === targetStatuses[0])?.display_name}
                  </span>
                ) : (
                  <span className="font-semibold">
                    {targetStatuses.length} statuses ({targetStatuses.map(sk => 
                      statuses?.find(s => s.status_key === sk)?.display_name
                    ).join(", ")})
                  </span>
                )
              ) : (
                <span className="font-semibold">selected statuses</span>
              )}
            </div>
          )}

          {/* Communication Channels */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Communication Channels*</Label>
              {!selectedTemplateId && (
                <span className="text-xs text-muted-foreground">Select a template first</span>
              )}
              {selectedTemplateId && supportedChannels.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {supportedChannels.length} available
                </Badge>
              )}
            </div>
            
            {selectedTemplateId && supportedChannels.length === 0 && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm">Selected template has no configured channels</span>
              </div>
            )}
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {COMMUNICATION_CHANNELS.map((channel) => {
                const isSupported = supportedChannels.includes(channel.value);
                const isDisabled = !selectedTemplateId || !isSupported;
                
                return (
                  <div key={channel.value} className="flex items-center space-x-2">
                    <Controller
                      name="channels_array"
                      control={form.control}
                      render={({ field }) => (
                        <Checkbox
                          id={channel.value}
                          checked={field.value?.includes(channel.value)}
                          disabled={isDisabled}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              field.onChange([...(field.value || []), channel.value]);
                            } else {
                              field.onChange(field.value?.filter(v => v !== channel.value));
                            }
                          }}
                        />
                      )}
                    />
                    <Label 
                      htmlFor={channel.value}
                      className={`text-sm font-normal cursor-pointer ${isDisabled ? 'text-muted-foreground' : ''}`}
                    >
                      {channel.label}
                      {selectedTemplateId && !isSupported && (
                        <span className="text-xs ml-1">(unavailable)</span>
                      )}
                    </Label>
                  </div>
                );
              })}
            </div>
            
            {/* Show warning for existing flows with unsupported channels */}
            {flowId && selectedTemplate && (
              (() => {
                const currentChannels = form.getValues("channels_array") || [];
                const unsupported = currentChannels.filter(ch => !supportedChannels.includes(ch));
                if (unsupported.length > 0) {
                  return (
                    <div className="flex items-center gap-2 p-3 bg-warning/10 text-warning rounded-md">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                      <span className="text-sm">
                        Warning: This flow uses {unsupported.join(", ")} but the template no longer supports {unsupported.length === 1 ? 'it' : 'them'}
                      </span>
                    </div>
                  );
                }
                return null;
              })()
            )}
          </div>

          {/* Recipients */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Label>Recipients*</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCheckRecipients}
                disabled={recipientLoading}
                className="ml-auto"
              >
                <Eye className="h-4 w-4 mr-1" />
                {recipientLoading ? "Checking..." : "Preview Recipients"}
              </Button>
            </div>

            {/* Recipient Types */}
            <div className="space-y-3">
              <Label className="text-sm">Recipient Types</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {RECIPIENT_TYPES.map((type) => (
                  <div key={type.value} className="flex items-center space-x-2">
                    <Controller
                      name="recipient_types"
                      control={form.control}
                      render={({ field }) => (
                        <Checkbox
                          id={type.value}
                          checked={field.value?.includes(type.value)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              field.onChange([...(field.value || []), type.value]);
                            } else {
                              field.onChange(field.value?.filter(v => v !== type.value));
                            }
                          }}
                        />
                      )}
                    />
                    <Label 
                      htmlFor={type.value}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {type.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Emails */}
            <div className="space-y-3">
              <Label className="text-sm">Custom Email Addresses</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter email address"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const input = e.target as HTMLInputElement;
                      handleAddCustomEmail(input.value);
                      input.value = "";
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    const input = (e.target as HTMLElement).parentElement?.querySelector('input');
                    if (input) {
                      handleAddCustomEmail(input.value);
                      input.value = "";
                    }
                  }}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {customEmails.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {customEmails.map((email) => (
                    <Badge key={email} variant="secondary" className="pr-1">
                      {email}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0 ml-1 hover:bg-destructive hover:text-destructive-foreground"
                        onClick={() => handleRemoveCustomEmail(email)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Recipient Preview */}
            {recipientPreview && (
              <div className="p-4 border rounded-lg bg-muted/50">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Recipient Preview
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-green-600" />
                    <span className="text-sm">
                      {recipientPreview.summary.email_count} with email
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-blue-600" />
                    <span className="text-sm">
                      {recipientPreview.summary.phone_count} with phone
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <span className="text-sm">
                      {recipientPreview.summary.missing_email_count} missing email
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <h5 className="text-sm font-medium">Recipients:</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                    {recipientPreview.recipients.map((recipient: any, index: number) => (
                      <div key={index} className="text-sm p-2 bg-background rounded border">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {recipient.type}
                          </Badge>
                          {recipient.name && (
                            <span className="font-medium">{recipient.name}</span>
                          )}
                        </div>
                        {recipient.email && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <Mail className="h-3 w-3" />
                            {recipient.email}
                          </div>
                        )}
                        {recipient.phone && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {recipient.phone}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Advanced Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="delay_minutes">Delay (minutes)</Label>
              <Input
                type="number"
                {...form.register("delay_minutes", { valueAsNumber: true })}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">
                Time to wait before triggering this flow
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Input
                type="number"
                {...form.register("priority", { valueAsNumber: true })}
                placeholder="1"
              />
              <p className="text-xs text-muted-foreground">
                Lower numbers execute first
              </p>
            </div>
          </div>

          {/* Active Toggle (only for editing) */}
          {flowId && (
            <div className="flex items-center space-x-2">
              <Controller
                name="is_active"
                control={form.control}
                render={({ field }) => (
                  <Switch
                    id="is_active"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createFlow.isPending || updateFlow.isPending}>
              {createFlow.isPending || updateFlow.isPending ? "Saving..." : "Save Flow"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
